import { getUserLocation, consoleLog, redirectProfileSlug } from "./helpers"
import { getLocationPlace } from "../services/placesConnector"
import {
    readCachedResolvedPlace,
    rememberResolvedPlace,
    syncUserLocation,
} from "./locationSync"
import { LOCALSTORAGE_PLACE, SESSIONSTORAGE_LOCATION } from "../const/storage"

const placeLabel = (place) =>
    [place?.name, place?.country].filter(Boolean).join(", ")

/**
 * Detects the user's current location from the browser, resolves it to a
 * compendium place, makes that the app's current place, and publishes it
 * (syncUserLocation). Behind the search bar's "Detect my current location"
 * icon, which is disabled while location permission is off.
 *
 * getLocationPlace is a paid Google chain (Geocoding, Autocomplete, Details),
 * so it is skipped when the user is still within the last resolution's
 * radius and that place is already the current one.
 * @param {Object} params
 * @param {Object} params.currentPlace - The app's current place state
 * @param {Function} params.setLocation
 * @param {Function} params.setPlace
 * @param {Function} params.setCountryCode
 * @param {Function} params.setToastMessage
 * @param {Function} params.setToastOptions
 * @param {Function} params.setShowToast
 * @param {Object} params.toastOptions
 * @param {Function} params.navigate - React Router navigate function
 * @returns {Promise<void>}
 */
export const handleLocationUpdate = async ({
    currentPlace,
    setLocation,
    setPlace,
    setCountryCode,
    setToastMessage,
    setToastOptions,
    setShowToast,
    toastOptions,
    navigate,
}) => {
    const showToast = (message, icon = "grow") => {
        setToastMessage(message)
        setToastOptions({ ...toastOptions, icon })
        setShowToast(true)
    }

    try {
        showToast("Detecting your location...", "border")

        const newLocation = await getUserLocation()
        if (!newLocation?.coordinates) {
            showToast(
                newLocation?.error?.message ||
                    "We couldn't detect your location. Please try again.",
            )
            return
        }

        const { coordinates } = newLocation
        consoleLog(
            `Detected location: ${coordinates.latitude}, ${coordinates.longitude}`,
        )
        setLocation(newLocation)
        sessionStorage.setItem(
            SESSIONSTORAGE_LOCATION,
            JSON.stringify(newLocation),
        )

        const cached = readCachedResolvedPlace(coordinates)
        if (cached?.id != null && cached.id === currentPlace?.id) {
            consoleLog("Still at the current place; skipping Google calls.")
            await syncUserLocation(coordinates)
            showToast(`Your location is up to date: ${placeLabel(currentPlace)}`)
            return
        }

        const newPlace = await getLocationPlace(newLocation)
        rememberResolvedPlace(coordinates, newPlace)
        if (!newPlace) {
            showToast("We can't find a place for your current location.")
            return
        }

        consoleLog("Detected place:", newPlace)
        setPlace(newPlace)
        localStorage.setItem(LOCALSTORAGE_PLACE, JSON.stringify(newPlace))
        const searchInput = document.getElementById("searchInputBox")
        if (searchInput) searchInput.placeholder = newPlace.name
        setCountryCode(newPlace.country_code)

        await syncUserLocation(coordinates)
        showToast(`Location updated: ${placeLabel(newPlace)}`)

        const pathParts = window.location.pathname.split("/")
        if (pathParts[1] === "analysis" && pathParts[2]) {
            redirectProfileSlug(newPlace, navigate)
        }
    } catch (error) {
        console.error("Error detecting the current location:", error)
        showToast("We couldn't update your location. Please try again.")
    }
}
